<template>
  <div class="preview-html-container">
    <div v-if="isLoading" class="text-center py-4">
      <a-icon type="sync" spin />
      <span class="ml-2">Loading baseline HTML...</span>
    </div>
    
    <div v-else-if="error" class="error-state">
      <a-alert type="error" showIcon>
        <template slot="message">
          {{ error }}
        </template>
      </a-alert>
      <a-button type="primary" size="small" @click="loadHtml" class="mt-3">
        Retry
      </a-button>
    </div>
    
    <div v-else class="code-viewer">
      <!-- Header bar -->
      <div class="code-header">
        <div class="file-info">
          <span class="file-name">Baseline HTML</span>
          <span class="line-count">{{ lineCount }} lines</span>
          <a-tooltip v-if="hasSuspiciousPatterns" title="Baseline might have issues">
            <a-icon type="warning" class="warning-icon" />
          </a-tooltip>
        </div>
        <div class="actions">
          <a-checkbox v-model="isBeautifyEnabled">
            Beautify Code
          </a-checkbox>
          <a-button type="link" size="small" @click="copyToClipboard" :loading="isCopying">
            <a-icon :type="copyIcon" />
            {{ copyText }}
          </a-button>
          <a-button type="link" size="small" @click="downloadHtml">
            <a-icon type="download" />
            Download
          </a-button>
        </div>
      </div>
      
      <!-- Code content -->
      <div class="code-content">
        <pre v-html="highlightedHtml"></pre>
      </div>
    </div>
  </div>
</template>

<script>
import WebpageService from '../services/webpage.service';
import beautify from 'js-beautify';
import { SUSPICIOUS_PATTERNS, checkForSuspiciousPatterns } from '@shared/utils/baselinePatterns';

export default {
  name: 'PreviewHtml',
  props: {
    urlId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      rawHtml: '',
      isBeautifyEnabled: true,
      isCopying: false,
      copyIcon: 'copy',
      copyText: 'Copy',
    };
  },
  computed: {
    displayHtml() {
      if (!this.rawHtml) return '';
      if (this.isBeautifyEnabled) {
        return beautify.html(this.rawHtml, {
          indent_size: 2,
          wrap_line_length: 120,
          preserve_newlines: true,
          max_preserve_newlines: 2,
          end_with_newline: false
        });
      }
      return this.rawHtml;
    },
    lineCount() {
      if (!this.displayHtml) return 0;
      return this.displayHtml.split('\n').length;
    },
    escapedHtml() {
      if (!this.displayHtml) return '';
      return this.escapeHtml(this.displayHtml);
    },
    hasSuspiciousPatterns() {
      if (!this.escapedHtml) return false;
      return checkForSuspiciousPatterns(this.escapedHtml);
    },
    highlightedHtml() {
      if (!this.escapedHtml) return '';
      
      // Apply highlighting for each suspicious pattern
      let result = this.escapedHtml;
      for (const pattern of SUSPICIOUS_PATTERNS) {
        result = result.replace(pattern, (match) => {
          return `<mark class="suspicious-highlight">${match}</mark>`;
        });
      }
      
      return result;
    }
  },
  mounted() {
    this.loadHtml();
  },
  methods: {
    escapeHtml(html) {
      const div = document.createElement('div');
      div.textContent = html;
      return div.innerHTML;
    },
    
    async loadHtml() {
      this.isLoading = true;
      this.error = null;
      
      try {
        const response = await WebpageService.getBaselineHtml(this.urlId);
        const pageResult = response.results && response.results[this.urlId];
        if (pageResult && pageResult.html) {
          this.rawHtml = pageResult.html;
        } else if (pageResult && pageResult.error) {
          this.error = pageResult.error;
        } else {
          this.error = 'Baseline HTML not found. The webpage may not have been baselined yet.';
        }
      } catch (err) {
        console.error('Failed to load baseline HTML:', err);
        if (err.response && err.response.status === 404) {
          this.error = 'Baseline HTML not found. The webpage may not have been baselined yet.';
        } else {
          this.error = 'Failed to load baseline HTML. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    async copyToClipboard() {
      this.isCopying = true;
      try {
        await navigator.clipboard.writeText(this.displayHtml);
        this.copyIcon = 'check';
        this.copyText = 'Copied!';

        // Reset back to original icon and text after 2 seconds
        setTimeout(() => {
          this.copyIcon = 'copy';
          this.copyText = 'Copy';
        }, 2000);
        
      } catch (err) {
        this.$message.error('Failed to copy to clipboard');
      } finally {
        this.isCopying = false;
      }
    },
    
    downloadHtml() {
      const blob = new Blob([this.displayHtml], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `baseline_${this.urlId}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<style lang="scss" scoped>
.preview-html-container {
  height: 100%;
  min-height: 400px;
}

.error-state {
  padding: 20px;
  text-align: center;
}

.code-viewer {
  display: flex;
  flex-direction: column;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #f7f7f7;
  border-bottom: 1px solid #d9d9d9;
  
  .file-info {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .file-name {
      font-weight: 600;
      color: #333;
    }
    
    .line-count {
      color: #666;
      font-size: 12px;
    }
    
    .warning-icon {
      color: #faad14;
      font-size: 16px;
      cursor: help;
    }
  }
  
  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .ant-btn-link {
      color: #666;
      
      &:hover {
        color: #04b0ed;
      }
    }
  }
}

.code-content {
  overflow: auto;
  padding: 10px;
  background-color: #e8e8e8;
  max-height: 600px;
  
  pre {
    margin: 0;
    overflow: visible;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    line-height: 1.5;
    color: #000;
    
    // Highlight for suspicious bot-block patterns (v-deep required for v-html content)
    ::v-deep .suspicious-highlight {
      background-color: #fff3cd;
      border-bottom: 2px solid #ff6b6b;
      padding: 0 2px;
      border-radius: 2px;
      color: #856404;
    }
  }
}
</style>
